import React from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { useForm } from 'react-hook-form'

interface CaptureCvvModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (cvv: string) => void
  brand?: string
}

interface CvvForm {
  cvv: string
}

// Amex usa CVV de 4 dígitos; sem bandeira conhecida aceita 3 ou 4
const getCvvRule = (brand?: string) => {
  const b = (brand || '').toLowerCase()
  if (b.includes('amex') || b.includes('american')) {
    return { pattern: /^\d{4}$/u, maxLength: 4, label: 'Código de 4 dígitos', placeholder: 'Ex. 1234', sample: '1234' }
  }
  if (b && b !== 'cartão') {
    return { pattern: /^\d{3}$/u, maxLength: 3, label: 'Código de 3 dígitos', placeholder: 'Ex. 123', sample: '123' }
  }
  return {
    pattern: /^\d{3,4}$/u,
    maxLength: 4,
    label: 'Código de 3 ou 4 dígitos',
    placeholder: 'Ex. 123',
    sample: '123'
  }
}

const CaptureCvvModal: React.FC<CaptureCvvModalProps> = ({ isOpen, onClose, onConfirm, brand }) => {
  const cvvRule = getCvvRule(brand)
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm<CvvForm>({
    defaultValues: { cvv: '' }
  })

  if (!isOpen) return null

  const submit = (data: CvvForm) => {
    onConfirm(data.cvv)
    onClose()
  }

  const modalContent = (
    <div
      className="fixed inset-0 z-[9999999] flex items-center justify-center bg-black/40"
      style={{
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999999,
        position: 'fixed',
        width: '100vw',
        height: '100vh'
      }}>
      <div className="relative w-full max-w-sm rounded-lg bg-white p-6 shadow-xl">
        <button
          type="button"
          aria-label="Fechar"
          onClick={onClose}
          className="absolute right-3 top-3 rounded-full border-0 bg-white p-1 text-neutral-600">
          <X className="h-5 w-5" />
        </button>
        {/* Título centralizado */}
        <div className="mb-4 text-center">
          <h3 className="text-lg font-semibold text-[#464F31]">Insira o CVV</h3>
          <p className="text-sm text-neutral-600">(Código de Verificação do Cartão)</p>
        </div>

        {/* Cartão fake com faixa preta e CVV "123" */}
        <div className="mb-6 rounded-xl bg-[#D9D3CA] p-4">
          <div className="mb-4 h-6 rounded bg-black" />
          <div className="relative h-10 rounded bg-white">
            <span
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-white px-2 py-0.5 text-sm font-medium text-red-500"
              style={{ border: '1px solid #F87171' }}>
              {cvvRule.sample}
            </span>
          </div>
        </div>

        {/* Formulário */}
        <form onSubmit={handleSubmit(submit)} className="space-y-4">
          <label htmlFor="cvv-input" className="block text-sm font-medium text-neutral-800">
            {cvvRule.label}
            <span className="text-red-500">*</span>
            <input
              id="cvv-input"
              type="password"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder={cvvRule.placeholder}
              {...register('cvv', {
                required: 'Informe o CVV',
                pattern: { value: cvvRule.pattern, message: `CVV inválido (${cvvRule.label.toLowerCase()})` }
              })}
              maxLength={cvvRule.maxLength}
              className="mt-1 h-11 w-full rounded-md bg-gray-100 px-3 text-sm text-gray-700 outline-none"
              style={{ border: '1px solid var(--Neutrals-Neutrals100, #E3E3E3)' }}
            />
          </label>
          {errors.cvv && <span className="text-xs text-red-600">{errors.cvv.message}</span>}

          {/* Botão de confirmação */}
          <button
            type="submit"
            disabled={isSubmitting}
            style={{ border: '1px solid var(--Primary-Primary800, #5E6C3B)' }}
            className="mt-2 w-full rounded-md bg-primary-800 py-3 text-sm font-medium text-white hover:bg-[#4c582f] disabled:opacity-60">
            {isSubmitting ? 'Enviando...' : 'Continuar'}
          </button>
        </form>
      </div>
    </div>
  )

  return createPortal(modalContent, document.documentElement)
}

export default CaptureCvvModal
